import { get } from 'utils/fetch';
import React, { useContext, useEffect, useState, useRef } from 'react';
import api from 'api';
import { GlobalContext } from 'GlobalContext';
import { Select, Space, message, Spin, Switch } from 'antd';
import { USD_TO_CNY, USDollar } from 'utils/format';
import { EditFilled, CheckOutlined } from '@ant-design/icons';
import { isPhoneSize } from 'utils/util';

const rowGap = 10;
const tableWidth = 700;
const backgroundColor = ['#ffffff', '#f9f9f9'];

function Progress(props) {
    const { percent } = props;

    const [curPercent, setCurPercent] = useState(0);

    useEffect(() => {
        setCurPercent(percent);
    }, [percent]);

    let barColor = '#1890ff'; // default
    if (percent < 30) {
        barColor = '#ff4d4f'; // red
    } else if (percent < 70) {
        barColor = '#faad14'; // orange/yellow
    } else {
        barColor = '#52c41a'; // green
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            <div
                style={{
                    width: '150px',
                    height: '10px',
                    backgroundColor: '#f0f0f0',
                    borderRadius: '4px',
                    overflow: 'hidden',
                    position: 'relative',
                    marginRight: '15px',
                }}
            >
                <div
                    style={{
                        width: `${curPercent}%`,
                        height: '100%',
                        backgroundColor: barColor,
                        transition: 'width 0.3s ease',
                        borderRadius: '4px',
                    }}
                ></div>
            </div>

            <div>{`${Math.round(curPercent)}%`}</div>
        </div>
    );
}

function KpiRow(props) {
    const {
        kpiInvestments,
        quarter,
        saleName,
        kpiAll,
        setKpiAll,
        saleId,
        fetchKpi,
    } = props;
    const { accessToken, refreshSession } = useContext(GlobalContext);
    const [edit, setEdit] = useState(false);
    function getTarget() {
        if (quarter === 0) {
            return kpiAll
                .filter((kpi) => kpi.sale_id === saleId)
                .reduce((sum, item) => {
                    return sum + item.target;
                }, 0);
        }
        const ret = kpiAll.find(
            (kpi) => kpi.sale_id === saleId && kpi.period === quarter
        );
        return ret ? ret.target : 0;
    }
    // const target = getTarget();
    const [target, setTarget] = useState(getTarget());
    const [initTarget, setInitTarget] = useState(target);

    useEffect(() => {
        const target = getTarget();
        setTarget(target);
        setInitTarget(target);
    }, [kpiAll]);
    // console.log(saleName, getQuarterName(quarter), getTarget());
    const investmentsCurSeason = kpiInvestments.filter((investment) => {
        if (quarter === 0) {
            return true;
        }
        return investment.quarter == quarter;
    });

    const totalAmount = investmentsCurSeason.reduce((sum, item) => {
        const amount = item.usd_equivalent;
        return sum + amount;
    }, 0);

    function getQuarterName(quarter) {
        switch (quarter) {
            case 0:
                return 'Year';
            case 1:
                return 'Q1';
            case 2:
                return 'Q2';
            case 3:
                return 'Q3';
            case 4:
                return 'Q4';
            default:
                return '';
        }
    }

    async function updateKpi(saleId, quarter, initTarget) {
        const response = await fetch(api.kpi.updateKpi, {
            method: 'POST',
            mode: 'cors',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
                saleId,
                quarter,
                initTarget,
            }),
        });

        fetchKpi();
        refreshSession();
    }

    // console.log('quarter', quarter);
    // console.log('investmentsCurSeason', totalAmount);
    // console.log(initTarget);
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                fontWeight: quarter === 0 ? 'bold' : '',
            }}
        >
            <div style={{ width: '100px', textAlign: 'center' }}>
                {getQuarterName(quarter)}
            </div>
            <div style={{ width: '100px', textAlign: 'center' }}>
                {USDollar.format(totalAmount)}
            </div>
            <div
                style={{
                    width: '150px',
                    justifyContent: 'center',
                    display: 'flex',
                }}
            >
                {!edit && <div>{USDollar.format(target)}</div>}
                {edit && (
                    <div>
                        <input
                            value={USDollar.format(initTarget)}
                            onChange={(e) => {
                                let value = e.target.value;
                                // Allow only digits and at most one `.`
                                let cleaned = value.replace(/[^0-9]/g, '');
                                setInitTarget(cleaned / 100);
                            }}
                            style={{
                                width: '120px',
                                border: 'none',
                                outline: 'none',
                                borderBottom: '1px solid black',
                                fontSize: '12px',
                                textAlign: 'center',
                            }}
                        ></input>
                    </div>
                )}
                {quarter !== 0 && (
                    <div
                        onClick={(e) => {
                            if (edit && initTarget !== target) {
                                updateKpi(saleId, quarter, initTarget);
                            }
                            setEdit(!edit);
                        }}
                    >
                        {edit ? <CheckOutlined /> : <EditFilled />}
                    </div>
                )}
                {quarter === 0 && <div style={{ width: '12px' }}></div>}
            </div>
            <div style={{ width: '200px' }}>
                <Progress
                    percent={target === 0 ? 100 : (totalAmount / target) * 100}
                />
            </div>
        </div>
    );
}

function SubKpi(props) {
    const { kpiInvestments, backgroundColor, kpiAll, setKpiAll, fetchKpi } =
        props;
    const saleName = kpiInvestments[0].sale_name;
    const saleId = kpiInvestments[0].sale_id;
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: backgroundColor,
                width: `${tableWidth}px`,
            }}
        >
            <div
                style={{
                    width: '100px',
                    textAlign: 'center',
                    alignSelf: 'center',
                }}
            >
                {saleName}
            </div>

            <div
                style={{
                    gap: `${rowGap}px`,
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {[1, 2, 3, 4, 0].map((quarter, idx) => (
                    <KpiRow
                        saleName={saleName}
                        key={idx}
                        kpiInvestments={kpiInvestments}
                        quarter={quarter}
                        kpiAll={kpiAll}
                        setKpiAll={setKpiAll}
                        saleId={saleId}
                        fetchKpi={fetchKpi}
                    />
                ))}
            </div>
        </div>
    );
}

function Header() {
    function HeaderItem(props) {
        const { width, text } = props;
        return (
            <div style={{ width: `${width}px`, textAlign: 'center' }}>
                {text}
            </div>
        );
    }
    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'row',
                backgroundColor: backgroundColor[1],
                width: `${tableWidth}px`,
                fontWeight: 'bold',
            }}
        >
            <HeaderItem width={100} text={'Sale'} />
            <HeaderItem width={100} text={'Period'} />
            <HeaderItem width={100} text={'Raised'} />
            <HeaderItem width={150} text={'Target'} />
            <HeaderItem width={200} text={'Progress'} />
        </div>
    );
}

export function KPI() {
    // investments of current year with sale
    const [kpiInvestments, setKpiInvestments] = useState([]);
    const [messageApi, contextHolder] = message.useMessage();
    const [kpiAll, setKpiAll] = useState([]);
    const [loading, setLoading] = useState(true);
    const { accessToken, refreshSession } = useContext(GlobalContext);
    const headerScrollRef = useRef(null);
    const mainScrollRef = useRef(null);

    const displayErrorMessage = (message) => {
        messageApi.open({
            type: 'error',
            content: message,
        });
    };

    async function fetchKpi() {
        await get(
            api.kpi.all,
            setKpiAll,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
    }

    async function fetchInvestments() {
        await get(
            api.kpi.currentYearInvestments,
            setKpiInvestments,
            (errors) =>
                errors.forEach((err) => {
                    displayErrorMessage(err.message);
                }),
            accessToken
        );
        refreshSession();
        setLoading(false);
    }

    useEffect(() => {
        if (accessToken === '') {
            return;
        }
        fetchKpi();
        fetchInvestments();
    }, [accessToken]);

    useEffect(() => {
        const header = headerScrollRef.current;
        const main = mainScrollRef.current;

        if (!header || !main) return;

        const syncScroll = (source, target) => () => {
            target.scrollLeft = source.scrollLeft;
        };

        const onHeaderScroll = syncScroll(header, main);
        const onMainScroll = syncScroll(main, header);

        header.addEventListener('scroll', onHeaderScroll);
        main.addEventListener('scroll', onMainScroll);

        return () => {
            header.removeEventListener('scroll', onHeaderScroll);
            main.removeEventListener('scroll', onMainScroll);
        };
    }, []);

    function Main() {
        return (
            <div
                style={
                    isPhoneSize()
                        ? {
                              height: 'calc(100vh - 338px)',
                          }
                        : {
                              height: 'calc(100vh - 118px)',
                          }
                }
            >
                <div
                    style={{
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        height: '100%',
                    }}
                >
                    <div
                        style={{
                            gap: `${rowGap}px`,
                            display: 'flex',
                            flexDirection: 'column',
                            marginTop: `${rowGap}px`,
                        }}
                    >
                        {[
                            ...new Set(
                                kpiInvestments.map((item) => item.sale_id)
                            ),
                        ]
                            .sort((a, b) => {
                                const sumA = kpiInvestments
                                    .filter(
                                        (investment) => investment.sale_id == a
                                    )
                                    .reduce((sum, item) => {
                                        return sum + item.amount;
                                    }, 0);

                                const sumB = kpiInvestments
                                    .filter(
                                        (investment) => investment.sale_id == b
                                    )
                                    .reduce((sum, item) => {
                                        return sum + item.amount;
                                    }, 0);
                                if (sumA < sumB) {
                                    return 1;
                                }
                                return -1;
                            })
                            .map((id, idx) => {
                                return (
                                    <div key={idx}>
                                        <SubKpi
                                            backgroundColor={
                                                backgroundColor[
                                                    idx % backgroundColor.length
                                                ]
                                            }
                                            kpiInvestments={kpiInvestments.filter(
                                                (investment) =>
                                                    investment.sale_id == id
                                            )}
                                            kpiAll={kpiAll}
                                            setKpiAll={setKpiAll}
                                            fetchKpi={fetchKpi}
                                        />
                                    </div>
                                );
                            })}
                    </div>
                </div>
            </div>
        );
    }

    function OverallDiv() {
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {/* Header scroll container */}
                <div style={{}}>
                    <div
                        ref={headerScrollRef}
                        style={{
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            scrollbarWidth: 'none',
                        }}
                    >
                        <div style={{ width: `${tableWidth}px` }}>
                            <Header />
                        </div>
                    </div>

                    {/* Main scroll container */}
                    <div
                        ref={mainScrollRef}
                        style={{
                            overflowX: 'auto',
                            overflowY: 'auto',
                            // height: 'calc(100vh - 100px)',
                        }}
                    >
                        <div style={{ width: `${tableWidth}px` }}>
                            <Main />
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {loading && (
                <div style={{ marginTop: '30px', textAlign: 'center' }}>
                    <Spin />
                </div>
            )}
            {!loading && (
                <div style={{ overflowX: 'auto', marginTop: '10px' }}>
                    <div style={{ width: 'max-content', margin: '0 auto' }}>
                        <OverallDiv />
                    </div>
                </div>
            )}
        </>
    );
}
